// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Center, Loader, Stack, Text } from '@mantine/core';
import { normalizeErrorString } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import { useScriptSureIFrame } from '@medplum/scriptsure-react';
import { normalizeAddressStates } from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import { IconAlertCircle, IconAlertTriangle } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';

interface ScriptSureIFrameProps {
  patientId?: string;
  title: string;
  loadingLabel: string;
  iframeHeight?: string | number;
}

interface ScriptSureIFrameAttemptProps extends ScriptSureIFrameProps {
  onError: (error: unknown) => void;
  onRetry: () => void;
}

type PatientStateGate = { status: 'checking' } | { status: 'ready' } | { status: 'blocked'; unresolved: string[] };

/**
 * ScriptSure rejects patients whose address state is not a two-letter USPS code. Before the patient sync starts,
 * rewrite resolvable values (e.g. "Texas" -> "TX") on the Patient and block when a value cannot be resolved.
 */
function usePatientStateGate(patientId: string | undefined, onError: (error: unknown) => void): PatientStateGate {
  const medplum = useMedplum();
  const [gate, setGate] = useState<PatientStateGate>(patientId ? { status: 'checking' } : { status: 'ready' });

  useEffect(() => {
    if (!patientId) {
      setGate({ status: 'ready' });
      return undefined;
    }
    let cancelled = false;
    setGate({ status: 'checking' });
    Sentry.startSpan(
      { op: 'scriptsure.patient-state-gate', name: 'ScriptSure patient state gate' },
      async (span): Promise<PatientStateGate> => {
        const patient = await medplum.readResource('Patient', patientId, { cache: 'no-cache' });
        const { addresses, changed, unresolved } = normalizeAddressStates(patient.address);
        span.setAttribute('unresolved', unresolved.length);
        span.setAttribute('normalized', changed);
        if (unresolved.length > 0) {
          return { status: 'blocked', unresolved };
        }
        if (changed) {
          await medplum.updateResource({ ...patient, address: addresses });
        }
        return { status: 'ready' };
      }
    )
      .then((nextGate: PatientStateGate) => {
        if (!cancelled) {
          setGate(nextGate);
        }
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          onError(reason);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, patientId, onError]);

  return gate;
}

function ScriptSureLoading({ label, status }: { label: string; status?: string }): JSX.Element {
  return (
    <Center mih={240}>
      <Stack align="center" gap="sm">
        <Loader aria-label={label} />
        <Text c="dimmed">{status}</Text>
      </Stack>
    </Center>
  );
}

function ScriptSureHostedIFrame({
  patientId,
  title,
  loadingLabel,
  iframeHeight,
  onError,
}: Omit<ScriptSureIFrameAttemptProps, 'onRetry'>): JSX.Element {
  const [status, setStatus] = useState<string | undefined>(loadingLabel);
  const iframeUrl = useScriptSureIFrame({
    patientId,
    onPatientSyncSuccess: useCallback(() => {
      setStatus('Preparing prescribing workspace…');
    }, []),
    onIframeSuccess: useCallback(() => {
      setStatus(undefined);
    }, []),
    onError,
  });

  if (!iframeUrl) {
    return <ScriptSureLoading label={loadingLabel} status={status} />;
  }

  return (
    <iframe
      title={title}
      src={iframeUrl}
      width="100%"
      height={typeof iframeHeight === 'number' ? iframeHeight : undefined}
      style={{ border: 0, height: iframeHeight ?? 800, display: 'block' }}
    />
  );
}

function ScriptSureIFrameAttempt({ onRetry, ...props }: ScriptSureIFrameAttemptProps): JSX.Element {
  const gate = usePatientStateGate(props.patientId, props.onError);

  if (gate.status === 'checking') {
    return <ScriptSureLoading label={props.loadingLabel} status="Checking patient address…" />;
  }

  if (gate.status === 'blocked') {
    return (
      <Alert icon={<IconAlertTriangle size={18} />} color="yellow" title="Patient address needs a valid US state">
        <Stack gap="sm">
          <Text>
            ScriptSure requires a two-letter US state code. The patient's address has an unrecognized state (
            {gate.unresolved.map((state: string) => `"${state}"`).join(', ')}). Update the address in the patient's
            profile, then try again.
          </Text>
          <Button variant="light" color="yellow" w="fit-content" onClick={onRetry}>
            Check again
          </Button>
        </Stack>
      </Alert>
    );
  }

  return <ScriptSureHostedIFrame {...props} />;
}

/**
 * Renders the ScriptSure hosted prescribing or provider-onboarding experience.
 * A patient ID starts a patient sync; omitting it opens ScriptSure's provider setup flow.
 */
export function ScriptSureIFrame({ patientId, title, loadingLabel, iframeHeight }: ScriptSureIFrameProps): JSX.Element {
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState<string>();
  const handleError = useCallback(
    (reason: unknown) => {
      Sentry.captureException(reason, {
        tags: { integration: 'scriptsure', surface: patientId ? 'prescribing' : 'provider-onboarding' },
      });
      setError(normalizeErrorString(reason));
    },
    [patientId]
  );
  const retry = useCallback(() => {
    setError(undefined);
    setAttempt((currentAttempt: number) => currentAttempt + 1);
  }, []);

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={18} />} color="red" title="Unable to open ScriptSure">
        <Stack gap="sm">
          <Text>{error}</Text>
          <Button variant="light" color="red" w="fit-content" onClick={retry}>
            Retry
          </Button>
        </Stack>
      </Alert>
    );
  }

  return (
    <ScriptSureIFrameAttempt
      key={attempt}
      patientId={patientId}
      title={title}
      loadingLabel={loadingLabel}
      iframeHeight={iframeHeight}
      onError={handleError}
      onRetry={retry}
    />
  );
}
